import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { formatApiError, limitWords, countWords, timeAgo } from "@/lib/format";
import { toast } from "sonner";

const MAX = 1000;

function Composer({ onPosted }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const post = async () => {
    if (!text.trim() || busy) return;
    setBusy(true);
    try {
      await api.post("/posts", { text });
      setText("");
      toast.success("contemplation shared");
      onPosted();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mt-3 mb-6">
      <textarea
        data-testid="member-post-input"
        value={text}
        onChange={(e) => setText(limitWords(e.target.value, text, MAX))}
        rows={5}
        placeholder="write a contemplation…"
        className="w-full p-4 text-sm resize-y"
        style={{ background: "transparent", border: "1px solid var(--border)", color: "var(--text-primary)" }}
      />
      <div className="flex items-center justify-between mt-2">
        <span className="text-xs tracking-widest" style={{ color: "var(--text-muted)" }} data-testid="member-post-word-count">{countWords(text)} / {MAX} words</span>
        <button onClick={post} disabled={!text.trim() || busy} data-testid="member-post-submit" className="btn px-8 py-3 text-sm tracking-widest">{busy ? "…" : "SHARE"}</button>
      </div>
    </div>
  );
}

function PostItem({ p, canDelete, onDeleted }) {
  const [busy, setBusy] = useState(false);
  const remove = async () => {
    if (busy || !window.confirm("delete this contemplation?")) return;
    setBusy(true);
    try {
      await api.delete(`/posts/${p.id}`);
      onDeleted();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setBusy(false);
    }
  };
  return (
    <li data-testid={`member-post-${p.id}`} className="py-5 px-4 fade-in-up" style={{ borderBottom: "1px solid var(--border)" }}>
      <p className="long-text text-sm leading-relaxed" style={{ color: "var(--text-primary)" }}>{p.text}</p>
      <div className="flex items-center gap-3 mt-3">
        <span className="text-xs" style={{ color: "var(--text-muted)" }}>{timeAgo(p.created_at)}</span>
        {canDelete && (
          <button onClick={remove} disabled={busy} data-testid={`member-post-delete-${p.id}`} className="btn ml-auto px-3 py-1 text-[10px] tracking-widest">{busy ? "…" : "DELETE"}</button>
        )}
      </div>
    </li>
  );
}

export function MemberPosts({ userId, canPost = false }) {
  const [items, setItems] = useState(null);
  const load = useCallback(() => api.get(`/users/${userId}/posts`).then(({ data }) => setItems(data)).catch(() => setItems([])), [userId]);
  useEffect(() => { load(); }, [load]);
  return (
    <div data-testid="member-posts">
      <label className="text-xs uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>contemplations</label>
      {canPost && <Composer onPosted={load} />}
      {!items ? (
        <p className="text-sm mt-3" style={{ color: "var(--text-muted)" }}>Loading…</p>
      ) : items.length === 0 ? (
        <p className="text-sm mt-3" style={{ color: "var(--text-muted)" }} data-testid="member-posts-empty">no contemplations yet.</p>
      ) : (
        <ul data-testid="member-posts-list">{items.map((p) => <PostItem key={p.id} p={p} canDelete={canPost} onDeleted={load} />)}</ul>
      )}
    </div>
  );
}
